import React, { useCallback, useState } from 'react';
import { AppButton, Box, FraudIcon, Preloader, Text, useQueryClient } from '@common-fe/common-fe';

import { Modal } from '@/components';
import { WarnModal } from '@/components/elements';
import useFraudConfirmationQuery from '@/modules/transaction/CardDetails/queries/useFraudConfirmation.query';
import { QUERY_KEYS } from '@/modules/transaction/Cards/queries/useGetCards.query';

interface Props {
  cardId: string;
  onSetVisible: (value: boolean) => void;
}

const ConfirmFraudOnCardModal: React.FC<Props> = ({ cardId, onSetVisible }) => {
  const queryClient = useQueryClient();
  const { confirmFraud, isConfirmFraudLoading } = useFraudConfirmationQuery();
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const handleConfirm = useCallback(async () => {
    try {
      await confirmFraud(cardId);
      handleClose();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CARDS_HOLDERS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CARDS_PURSES] });
    } catch {
      setIsErrorModalVisible(true);
    }
  }, [confirmFraud, handleClose, cardId, queryClient]);

  if (isErrorModalVisible) {
    return (
      <WarnModal
        testId="confirm-fraud-on-card_error"
        visible={isErrorModalVisible}
        onSetVisible={setIsErrorModalVisible}
        header="The server encountered an error processing the request"
        helptext={`
          It does not appear to have affected your data, but we cannot process your request.
          Our technical stuff has been automatically and will be looking into this with the utmost urgency.
        `}
        buttonText="Close"
        onSubmit={handleConfirm}
        submitButtonText="Try again"
        isAwaitMode
        disableAutomaticClosing
      />
    );
  }

  return (
    <Modal testId="confirm-fraud-on-card" visible onSetVisible={onSetVisible}>
      <Box align="center">
        <FraudIcon size="48px" color="iconPrimary" />

        <Text
          data-testId="confirm-fraud-on-card_title"
          margin={{ top: 'spacing12' }}
          size="2xl"
          textAlign="center"
          weight={700}
        >
          Fraud has been confirmed on this card?
        </Text>
        <Text
          data-testId="confirm-fraud-on-card_sub-title"
          margin={{ top: 'spacing12' }}
          size="medium"
          textAlign="center"
          weight={400}
        >
          Participant will no longer be notified or have a view into any future transactions on a card with confirmed fraud. All future transactions on this card will continue to be declined.
        </Text>

        <Box
          margin={{ top: 'spacing24' }}
          direction="row"
          gap="spacing12"
          width="100%"
          justify="center"
        >
          <Box width={{ max: '150px', width: '100%' }}>
            <AppButton
              testId="confirm-fraud-on-card_close"
              width="100%"
              buttonType="secondary"
              onClick={handleClose}
            >
              Cancel
            </AppButton>
          </Box>

          <Box width={{ max: '150px', width: '100%' }}>
            <AppButton
              testId="confirm-fraud-on-card_confirm"
              width="100%"
              buttonType="primary"
              color="red"
              disabled={isConfirmFraudLoading}
              onClick={handleConfirm}
            >
              {isConfirmFraudLoading ? <Preloader color="white" /> : 'Confirm'}
            </AppButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmFraudOnCardModal;
